import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Check, Lock, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { MentionText } from '@/components/mentions/MentionText';

interface PollOption {
  id: string;
  text: string;
  order: number;
  voteCount: number;
  percentage: number;
}

interface PollAuthor {
  id: string;
  name: string;
  image: string | null;
}

interface PollData {
  id: string;
  question: string;
  isAnonymous: boolean;
  allowMultiple?: boolean;
  expiresAt: string | null;
  createdAt: string;
  author: PollAuthor;
}

interface PollCardProps {
  communityId: string;
  poll: PollData;
  options: PollOption[];
  userVotes?: string[];
  userVote?: string | null;
  totalVotes: number;
  voters?: Array<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
    image: string | null;
    optionId: string;
    optionText: string;
  }>;
  showHeader?: boolean;
  onInteracted?: () => void;
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

function applyPercentages(opts: PollOption[]): { options: PollOption[]; totalVotes: number } {
  const cleaned = opts.map((opt) => ({
    ...opt,
    voteCount: Math.max(0, Number(opt.voteCount) || 0),
  }));
  const total = cleaned.reduce((sum, opt) => sum + opt.voteCount, 0);
  if (total <= 0) {
    return {
      options: cleaned.map((opt) => ({ ...opt, percentage: 0 })),
      totalVotes: 0,
    };
  }

  const raw = cleaned.map((opt) => (opt.voteCount / total) * 100);
  const floors = raw.map((pct) => Math.floor(pct));
  const remainder = 100 - floors.reduce((sum, pct) => sum + pct, 0);
  const ranked = raw
    .map((pct, index) => ({ index, frac: pct - Math.floor(pct) }))
    .sort((a, b) => b.frac - a.frac);
  const percentages = [...floors];
  for (let i = 0; i < remainder; i++) {
    percentages[ranked[i].index] += 1;
  }

  return {
    options: cleaned.map((opt, index) => ({ ...opt, percentage: percentages[index] })),
    totalVotes: total,
  };
}

function normalizeOptions(opts: PollOption[]): PollOption[] {
  const map = new Map<string, PollOption>();
  for (const opt of opts) {
    if (!opt?.id) continue;
    const voteCount = Math.max(0, Number(opt.voteCount) || 0);
    const existing = map.get(opt.id);
    if (!existing) {
      map.set(opt.id, { ...opt, voteCount, percentage: 0 });
    } else {
      existing.voteCount = Math.max(existing.voteCount, voteCount);
    }
  }
  return applyPercentages([...map.values()].sort((a, b) => a.order - b.order)).options;
}

function recalcOptions(
  opts: PollOption[],
  prevSelected: string[],
  nextSelected: string[]
): { options: PollOption[]; totalVotes: number } {
  const prev = uniqueIds(prevSelected);
  const next = uniqueIds(nextSelected);
  const updated = normalizeOptions(opts).map((opt) => {
    let count = opt.voteCount;
    const wasSelected = prev.includes(opt.id);
    const willBeSelected = next.includes(opt.id);
    if (wasSelected && !willBeSelected) count = Math.max(0, count - 1);
    if (!wasSelected && willBeSelected) count = count + 1;
    return { ...opt, voteCount: count };
  });
  return applyPercentages(updated);
}

export function PollCard({
  communityId,
  poll,
  options,
  userVotes,
  userVote,
  totalVotes: initialTotalVotes,
  voters: initialVoters = [],
  showHeader = false,
  onInteracted,
}: PollCardProps) {
  const initialVotesValue = useMemo<string[]>(() => {
    if (userVotes && userVotes.length > 0) return uniqueIds(userVotes);
    if (userVote) return [userVote];
    return [];
  }, [userVotes, userVote]);

  const safeOptions = useMemo(
    () => normalizeOptions(Array.isArray(options) ? options : []),
    [options]
  );
  const allowMultiple = Boolean(poll.allowMultiple);

  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialVotesValue);
  const [localOptions, setLocalOptions] = useState<PollOption[]>(safeOptions);
  const [localTotalVotes, setLocalTotalVotes] = useState(
    () => applyPercentages(safeOptions).totalVotes
  );
  const [hasVoted, setHasVoted] = useState<boolean>(initialVotesValue.length > 0);
  const [resultsVisible, setResultsVisible] = useState(
    () => initialVotesValue.length > 0 || initialTotalVotes > 0
  );
  const [showVoters, setShowVoters] = useState(false);
  const [votersList, setVotersList] = useState(initialVoters);
  const [isLoadingVoters, setIsLoadingVoters] = useState(false);
  const [selectedOptionFilter, setSelectedOptionFilter] = useState<string | null>(null);

  const pendingVoteRef = useRef(false);
  const localOptionsRef = useRef(localOptions);
  const selectedOptionsRef = useRef(selectedOptions);
  const hasVotedRef = useRef(hasVoted);

  useEffect(() => {
    localOptionsRef.current = localOptions;
  }, [localOptions]);
  useEffect(() => {
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);
  useEffect(() => {
    hasVotedRef.current = hasVoted;
  }, [hasVoted]);

  useEffect(() => {
    setSelectedOptions(initialVotesValue);
    setHasVoted(initialVotesValue.length > 0);
  }, [initialVotesValue]);

  useEffect(() => {
    if (initialVoters && initialVoters.length > 0) {
      setVotersList(initialVoters);
    }
  }, [initialVoters]);

  const refreshFromServer = useCallback(async () => {
    if (!poll.id || !communityId) return;
    try {
      const res = await axiosInstance.get(`/api/communities/${communityId}/polls/${poll.id}`);
      if (res.data?.voters) setVotersList(res.data.voters);
      const serverVotes = Array.isArray(res.data?.userVotes)
        ? uniqueIds(res.data.userVotes)
        : res.data?.userVote
          ? [res.data.userVote]
          : [];
      setSelectedOptions(serverVotes);
      selectedOptionsRef.current = serverVotes;
      setHasVoted(serverVotes.length > 0);
      if (Array.isArray(res.data?.options)) {
        const nextOptions = normalizeOptions(res.data.options);
        const { options: balanced, totalVotes } = applyPercentages(nextOptions);
        setLocalOptions(balanced);
        localOptionsRef.current = balanced;
        setLocalTotalVotes(totalVotes);
      }
    } catch (err) {
      console.error('Failed to refresh poll:', err);
    }
  }, [communityId, poll.id]);

  const submitVote = useCallback(
    async (previousIds: string[], nextIds: string[]) => {
      if (pendingVoteRef.current) return;
      pendingVoteRef.current = true;

      const prev = uniqueIds(previousIds);
      const next = uniqueIds(nextIds);
      const { options: nextOptions, totalVotes: nextTotal } = recalcOptions(
        localOptionsRef.current,
        prev,
        next
      );
      localOptionsRef.current = nextOptions;
      selectedOptionsRef.current = next;
      setLocalOptions(nextOptions);
      setLocalTotalVotes(nextTotal);
      setSelectedOptions(next);
      setHasVoted(next.length > 0);
      setResultsVisible(true);

      try {
        const payload = {
          ...(next.length > 0 ? { optionId: next[next.length - 1] } : {}),
          optionIds: next,
        };
        await axiosInstance.post(`/api/communities/${communityId}/polls/${poll.id}/vote`, payload);
        onInteracted?.();
        if (!poll.isAnonymous) {
          await refreshFromServer();
        }
      } catch (error) {
        selectedOptionsRef.current = prev;
        localOptionsRef.current = safeOptions;
        setSelectedOptions(prev);
        setLocalOptions(safeOptions);
        setLocalTotalVotes(applyPercentages(safeOptions).totalVotes);
        setHasVoted(prev.length > 0);
        console.error('Failed to vote', error);
        const message =
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to record vote';
        toast.error(message);
      } finally {
        pendingVoteRef.current = false;
      }
    },
    [communityId, poll.id, poll.isAnonymous, safeOptions, refreshFromServer, onInteracted]
  );

  const handleOptionClick = useCallback(
    (optionId: string) => {
      if (pendingVoteRef.current) return;
      const expired = poll.expiresAt && new Date(poll.expiresAt) < new Date();
      if (expired) return;

      const current = uniqueIds(selectedOptionsRef.current);

      if (allowMultiple) {
        const next = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
        void submitVote(current, next);
      } else if (current[0] === optionId) {
        void submitVote(current, []);
      } else {
        void submitVote(current, [optionId]);
      }
    },
    [allowMultiple, poll.expiresAt, submitVote]
  );

  const toggleVoters = async (optionIdFilter?: string) => {
    if (optionIdFilter !== undefined) {
      setSelectedOptionFilter(optionIdFilter);
    } else {
      setSelectedOptionFilter(null);
    }

    if (showVoters && optionIdFilter === undefined) {
      setShowVoters(false);
      return;
    }

    setShowVoters(true);

    if (votersList.length === 0 && poll.id && communityId) {
      setIsLoadingVoters(true);
      try {
        const res = await axiosInstance.get(`/api/communities/${communityId}/polls/${poll.id}`);
        if (res.data?.voters) setVotersList(res.data.voters);
      } catch (err) {
        console.error('Failed to fetch poll voters:', err);
      } finally {
        setIsLoadingVoters(false);
      }
    }
  };

  const authorName = poll.author?.name || 'Anonymous';
  const authorInitials =
    authorName
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  const hasExpired = !!(poll.expiresAt && new Date(poll.expiresAt) < new Date());
  const showResults = hasVoted || hasExpired || resultsVisible;

  const getRemainingText = () => {
    if (!poll.expiresAt) return null;
    const exp = new Date(poll.expiresAt);
    if (exp < new Date()) return 'Poll ended';
    return `${formatDistanceToNow(exp)} remaining`;
  };

  const filteredVoters = selectedOptionFilter
    ? votersList.filter((v) => v.optionId === selectedOptionFilter)
    : votersList;

  return (
    <Card className="w-full border border-border/40 bg-card/80 shadow-sm hover:shadow transition-all duration-200 overflow-hidden rounded-2xl">
      {showHeader && (
        <div className="p-4 border-b border-border/30 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border/40">
              <AvatarImage src={poll.author?.image || undefined} alt={authorName} />
              <AvatarFallback className="bg-slate-700 text-white font-bold text-xs">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-sm text-foreground">{authorName}</h3>
              <p className="text-[11px] text-muted-foreground">
                {poll.createdAt
                  ? formatDistanceToNow(new Date(poll.createdAt), { addSuffix: true })
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {allowMultiple && (
              <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
                <ListChecks className="h-3 w-3" /> Multi-select
              </span>
            )}
            {poll.isAnonymous && (
              <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-muted font-medium text-muted-foreground">
                <Lock className="h-3 w-3" /> Anonymous
              </span>
            )}
          </div>
        </div>
      )}

      <div className="p-4 space-y-3">
        <div>
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-base sm:text-lg font-bold text-foreground leading-snug">
              <MentionText text={poll.question} />
            </h2>
            {allowMultiple && !showHeader && (
              <span className="shrink-0 flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                <ListChecks className="h-3 w-3" /> Multi-select
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 font-medium">
            {localTotalVotes} {localTotalVotes === 1 ? 'vote' : 'votes'}
            {!showResults && ' · Vote to see results'}
          </p>
        </div>

        <div className="space-y-2">
          {localOptions.length > 0 ? (
            localOptions.map((option) => {
              const isSelected = selectedOptions.includes(option.id);
              const pct = Number(option.percentage) || 0;
              const optionVoters = votersList.filter((v) => v.optionId === option.id);

              return (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option.id)}
                  className={cn(
                    'relative overflow-hidden rounded-xl py-2.5 px-3.5 border border-border/40 bg-muted/20 transition-all duration-200 flex items-center justify-between gap-3 select-none text-foreground',
                    hasExpired
                      ? 'cursor-not-allowed opacity-80'
                      : 'cursor-pointer active:scale-[0.995]'
                  )}
                >
                  {showResults && (
                    <div
                      className={cn(
                        'absolute left-0 top-0 bottom-0 transition-all duration-500 pointer-events-none',
                        isSelected ? 'bg-primary/75' : 'bg-primary/30'
                      )}
                      style={{ width: `${Math.max(pct, 0)}%` }}
                    />
                  )}

                  <div className="relative z-10 flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="text-sm font-medium truncate">{option.text}</span>
                  </div>

                  <div className="relative z-10 flex items-center gap-2 shrink-0">
                    {!poll.isAnonymous && optionVoters.length > 0 && showResults && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVoters(option.id);
                        }}
                        className="flex items-center -space-x-2 cursor-pointer hover:opacity-85 transition-opacity py-0.5 px-1 rounded-full hover:bg-background/40"
                      >
                        {optionVoters.slice(0, 3).map((voter, idx) => (
                          <Avatar
                            key={idx}
                            className="h-6 w-6 border-2 border-background ring-1 ring-border/40 shrink-0"
                          >
                            <AvatarImage src={voter.image || undefined} />
                            <AvatarFallback className="text-[9px] bg-slate-700 text-white font-bold">
                              {`${voter.firstName?.[0] || ''}${voter.lastName?.[0] || ''}`.slice(
                                0,
                                2
                              )}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                    )}
                    {showResults && (
                      <span className="text-xs font-bold min-w-[36px] text-right text-muted-foreground">
                        {Math.round(pct)}%
                      </span>
                    )}
                    {isSelected && (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-background/90 text-primary shadow-sm">
                        <Check className="h-3 w-3 stroke-[3]" />
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm">
              No options available
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/20">
          <span className="flex items-center gap-1 font-medium">
            {poll.isAnonymous ? (
              <>
                <Lock className="h-3 w-3" /> Anonymous Voting
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" /> Open Voting
              </>
            )}
          </span>
          {getRemainingText() && <span className="font-medium">⏱ {getRemainingText()}</span>}
        </div>

        {allowMultiple && !hasExpired && (
          <p className="text-[11px] text-muted-foreground">
            Tap multiple options to vote for more than one. Tap again to unselect.
          </p>
        )}

        {showVoters && !poll.isAnonymous && (
          <div className="pt-3 border-t border-border/30 space-y-2 animate-in fade-in duration-200">
            <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
              <span>
                {selectedOptionFilter
                  ? `Voters (${filteredVoters.length})`
                  : `All Voters (${votersList.length})`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVoters(false)}
                className="h-auto p-0 text-xs text-primary hover:bg-transparent"
              >
                Hide
              </Button>
            </div>
            {isLoadingVoters ? (
              <p className="text-xs text-muted-foreground py-2">Loading voters...</p>
            ) : filteredVoters.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                {filteredVoters.map((voter, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/30"
                  >
                    <Avatar className="h-6 w-6 shrink-0">
                      <AvatarImage src={voter.image || undefined} />
                      <AvatarFallback className="text-[10px] bg-slate-700 text-white font-bold">
                        {`${voter.firstName?.[0] || ''}${voter.lastName?.[0] || ''}`.slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">
                        {`${voter.firstName || ''} ${voter.lastName || ''}`.trim()}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {voter.optionText}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">No voters for this option yet</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
